import React, { useCallback, useEffect, useState, useRef } from 'react';

import {
  love,
  dropDownMenuOnPhoto,
  clickSaveCollection,
  popupOpened,
  createCollection,
} from '@wix/bi-logger-photo-ugc/v2';
import { LoveActions } from '../../../../common/LoveService';
import { CollectionInfoModal } from '../../../../common/components/modals/CollectionInfo/CollectionInfoModal';
import { AddToList } from './AddToList';
import { st, classes } from '../galleryWidget.st.css';
import { useBi, useEnvironment, useExperiments } from '@wix/yoshi-flow-editor';
import { Popover, Text } from 'wix-ui-tpa';
import { ChevronDown } from 'wix-ui-icons-common';
import cx from 'classnames';

type GalleryFavoritesListsComponentsProps = {
  galleryId: string;
  currentItemCollections?: ItemMediaCollection[];
  currentItemId: string;
  navigator: INavigator;
  loveActions: LoveActions;
  isAddToListModalOpen: boolean;
  isAddToListPopoverOpen: boolean;
  isCreateModalOpen: boolean;
  biDefaultParams: any;
  closeAddToList: () => void;
  openCreateModal: () => void;
  closeCreateModal: () => void;
  noCollections?: boolean;
};

export const useFavoritesLists = ({
  loggedIn,
  getLoginPromise,
  biDefaultParams,
  galleryId,
  currentCollectionName,
  loveActions,
  currentItemCollections,
  compId,
  navigator,
}: {
  galleryId: string;
  currentItemCollections?: ItemMediaCollection[];
  navigator: INavigator;
  loveActions: LoveActions;
  biDefaultParams: any;
  currentCollectionName: string;
  compId: string;
  loggedIn: boolean;
  getLoginPromise: (data: string) => Promise<any>;
  collectionsFetched?: boolean;
}) => {
  const { isMobile } = useEnvironment();
  const biLogger = useBi();
  const [isAddToListPopoverOpen, setAddToListPopoverOpen] = useState(false);
  const [isAddToListModalOpen, setAddToListModalOpen] = useState(false);
  const [isCreateModalOpen, setCreateModalOpen] = useState(false);
  const [pendingFetchCollections, setPendingFetchCollections] = useState(false);
  const [currentItemId, setCurrentItemId] = useState('');
  const clickOrigin = useRef('gallery');
  const { experiments } = useExperiments();

  const noCollections =
    currentItemCollections && currentItemCollections.length === 0;

  const biDefaultParamsForLove = {
    ...biDefaultParams,
    origin: noCollections
      ? 'Live site heart on image'
      : 'Live site heart dropdown',
  };

  useEffect(() => {
    setPendingFetchCollections(false);
  }, [currentItemCollections]);

  const fetchCollections = (id: string) => {
    setPendingFetchCollections(true);
    setCurrentItemId(id);
    loveActions.fetchItemCollections(id);
  };

  const openAddToListPopover = (id: string) => {
    fetchCollections(id);
    setAddToListPopoverOpen(true);
  };

  const openAddToListModal = (id: string) => {
    fetchCollections(id);
    setAddToListModalOpen(true);
  };

  const closeAddToList = () => {
    setAddToListPopoverOpen(false);
    setAddToListModalOpen(false);
  };
  const openCreateModal = () => {
    setCreateModalOpen(true);
  };
  const closeCreateModal = () => {
    setCreateModalOpen(false);
  };

  const loginAndOpenAddToList = async (id: string, type: string) => {
    const openAddToList =
      type === 'modal' ? openAddToListModal : openAddToListPopover;
    if (!loggedIn) {
      try {
        const logInPromise = getLoginPromise(
          JSON.stringify({ showInfoModal: true, origin: 'add image' }),
        );
        await logInPromise;
      } catch (e: any) {
        console.error('Not logged in', e);
        return;
      }
    } else {
      openAddToList(id);
    }
  };

  const onDropdownClicked = useCallback(
    async (id: string) => {
      loginAndOpenAddToList(id, 'popover');
      biLogger.report(dropDownMenuOnPhoto(biDefaultParams));
    },
    [biDefaultParams, biLogger, galleryId, loginAndOpenAddToList],
  );

  const onLoveClicked = async (id: string, origin: string) => {
    noCollections &&
      biLogger.report(
        createCollection({
          ...biDefaultParams,
          origin: 'Live site heart on image',
        }),
      );
    if (typeof id !== 'string') {
      return;
    }
    biLogger.report(
      love({
        ...biDefaultParams,
        galleryType: 'photo album',
        gallery_id: galleryId,
      }),
    );
    if (isMobile) {
      loginAndOpenAddToList(id, 'modal');
    } else {
      if (!currentCollectionName) {
        await loginAndOpenAddToList(id, 'popover');
        loveActions.init(id);
      } else {
        loveActions.toggleLove(id);
        biLogger.report(
          clickSaveCollection({
            ...biDefaultParams,
            origin: 'love button',
          }),
        );
      }
    }
  };
  const getFavoritesListDropDown =
    currentCollectionName && !isMobile
      ? (id: string, origin: string) => {
          return (
            <Popover
              tabIndex={-1}
              shown={
                isAddToListPopoverOpen &&
                clickOrigin.current === origin &&
                id === currentItemId &&
                !pendingFetchCollections
              }
              placement="top-start"
              className={classes.popover}
              // triggerAction=""
              showArrow={false}
              appendTo="window"
              contentClassName={classes.popoverContent}
              onEscPress={closeAddToList}
              onTabOut={closeAddToList}
              onClickOutside={closeAddToList}
              width={360}
              maxWidth={360}
              wiredToSiteColors={false}
            >
              <Popover.Element>
                <div
                  className={cx([
                    'info-member',
                    'info-element-social-love-count',
                    'dropdown-container',
                    st(classes.dropDownContainer, {
                      isFullscreen: origin === 'fullscreen',
                    }),
                  ])}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    clickOrigin.current = origin;
                    onDropdownClicked && onDropdownClicked(id);
                    biLogger.report(clickSaveCollection(biDefaultParams));
                  }}
                >
                  <Text className={`currentFavoriteList ${origin}`}>
                    {currentCollectionName}
                  </Text>
                  <ChevronDown
                    className={`currentFavoriteListIcon ${origin}`}
                  />
                </div>
              </Popover.Element>
              <Popover.Content>
                <AddToList
                  biDefaultParams={biDefaultParams}
                  isOpen={isAddToListPopoverOpen && !noCollections}
                  onClose={closeAddToList}
                  itemCollections={currentItemCollections}
                  currentItemId={currentItemId}
                  galleryId={galleryId}
                  navigator={navigator}
                  loveActions={loveActions}
                  onCreate={openCreateModal}
                  className={compId}
                />
              </Popover.Content>
            </Popover>
          );
        }
      : undefined;

  const shouldOpenAddToModal = isAddToListModalOpen;
  const addToListIsOpenWithoutItems =
    (isAddToListPopoverOpen || isAddToListModalOpen) && noCollections;
  const shouldOpenCreateModal =
    !pendingFetchCollections &&
    (addToListIsOpenWithoutItems || isCreateModalOpen);
  const favoritesListsElements = (
    <>
      {shouldOpenAddToModal && (
        <AddToList
          biDefaultParams={biDefaultParams}
          isOpen={isAddToListModalOpen}
          onClose={closeAddToList}
          itemCollections={currentItemCollections}
          currentItemId={currentItemId}
          galleryId={galleryId}
          navigator={navigator}
          loveActions={loveActions}
          onCreate={openCreateModal}
          className={compId}
        />
      )}
      {shouldOpenCreateModal && (
        <CollectionInfoModal
          onClose={() => {
            closeCreateModal();
            closeAddToList();
          }}
          onSave={async ({
            name,
            privacy_settings,
            description,
          }: {
            name?: string;
            privacy_settings?: PrivacySettings;
            description?: string;
          }) => {
            // onClose();
            loveActions.createCollectionAndLove(
              currentItemId,
              JSON.stringify({
                name,
                privacySettings: privacy_settings,
                description,
              }),
              biDefaultParamsForLove,
            );
            closeCreateModal();
            closeAddToList();
          }}
          isCreateCollection={true}
          biDefaultParams={biDefaultParamsForLove}
        />
      )}
    </>
  );

  return { onLoveClicked, getFavoritesListDropDown, favoritesListsElements };
};
